import {
  useResponseRedirect,
  useRedirectToPage,
  useRoleForBorrower,
} from "src/hooks";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import { useWebClientApi } from "src/api";
import {
  fromHomeUsage,
  toCreateHomeUsageReq,
  toUpdateHomeUsageReq,
  type HomeUsageFormData,
} from "src/api/adapters";
import FormLayout from "src/layouts/FormLayout";
import { Col } from "src/components/layout";
import { useMemo, useRef } from "react";
import { validate, type ValidationMapperFunc } from "src/utils";
import { testOtherTextRequired } from "src/forms/validation/conditionalOther";
import { DLA_HOME_USAGE_OTHER } from "src/forms/validation/conditionalOtherConfigs";
import {
  FormTable,
  FormTableBody,
  HomeFields,
  Target02Icon,
} from "@porchpass/ui";
import { DomainHomeUsageType } from "@porchpass/api-client";
import type { DtoHome } from "@porchpass/api-client";
import type { HomeFieldsFormData } from "@porchpass/ui";
import {
  fromHomeUsageFormData,
  homeUsageErrorsAfterSubmit,
  toHomeUsageFormData,
} from "src/forms/adapters/home";
import { fieldErrorsAfterSubmit } from "src/forms/fieldErrorsAfterSubmit";

const validationMapper: ValidationMapperFunc<HomeUsageFormData> = (
  formData,
) => ({
  usage_type_other: [
    testOtherTextRequired(
      formData.usage_type,
      DLA_HOME_USAGE_OTHER,
      undefined,
      "Must enter a use",
    ),
  ],
});

export default () => {
  const api = useWebClientApi();
  const role = useRoleForBorrower();
  const coborrowerNavigate = useRedirectToPage("/ethnicity");
  if (role === "COBORROWER") {
    coborrowerNavigate();
  }
  const navigateToPlacement = useRedirectToPage("/home/placement");
  const { formData, updateField, hasLoaded } = useFormLoad<HomeUsageFormData>(
    {},
    () => api.getHome(),
    (data) => fromHomeUsage(data as DtoHome),
  );
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const [submitHandler, loading, response, hasAttemptedFormSubmission] =
    useDlaFormSubmit(
      (data: HomeUsageFormData) => {
        const merged = { ...formDataRef.current, ...data };
        // Match legacy endpoint-config default: CREATE until home record exists.
        if (formDataRef.current.user_first_visit !== false) {
          return api.createHome(toCreateHomeUsageReq(merged));
        }
        return api.updateHome(toUpdateHomeUsageReq(merged));
      },
      { onSuccess: () => navigateToPlacement() },
    );
  useResponseRedirect("/home/placement", response);

  const formErrors = validate<HomeUsageFormData>(formData, validationMapper);
  const homeValue = useMemo(
    () => toHomeUsageFormData(formData ?? {}),
    [formData],
  );

  const visibleErrors = fieldErrorsAfterSubmit(
    homeUsageErrorsAfterSubmit(formErrors, hasAttemptedFormSubmission),
    hasAttemptedFormSubmission,
  );

  const handleChange = (patch: Partial<HomeFieldsFormData>) => {
    const next = { ...homeValue, ...patch };
    const payload = fromHomeUsageFormData(next);
    for (const [key, value] of Object.entries(payload)) {
      updateField(key as keyof HomeUsageFormData)(value);
    }
  };

  return (
    <FormLayout
      title="What will your new home be used as?"
      headerTitle="Home and Land"
      stepIndex={7}
      footerTitle={formData?.usage_type === "OTHER" ? "Next" : ""}
      clickHandler={() => void submitHandler(formData, formErrors)}
      isLoading={loading}
    >
      <Col>
        <FormTable
          title="Primary Use"
          subtitle="How will this home be used"
          headerIcon={
            <Target02Icon
              variant="bulk-rounded"
              size={16}
              className="text-[#226392]"
            />
          }
        >
          <FormTableBody>
            <HomeFields
              value={homeValue}
              onChange={handleChange}
              errors={visibleErrors}
              showPrimaryUseOnly
              onPrimaryUseSelection={(usage) => {
                // Before the saved home loads we can't tell create from update
                if (hasLoaded && usage !== DomainHomeUsageType.HomeUsageOther) {
                  void submitHandler({
                    usage_type: usage,
                  });
                }
              }}
            />
          </FormTableBody>
        </FormTable>
      </Col>
    </FormLayout>
  );
};
